import styled from "styled-components";

/**
 * Wrapper do DatePickerWithInput (Mobile)
 * Configura o layout principal do componente no contexto mobile.
 */
export const WrapperMobile = styled.div`
  position: relative;
  height: inherit;
  width: 100%;
`;

/**
 * Título no DatePicker (Mobile)
 * Exibido quando o campo está aberto.
 */
export const WrapperTitleMobile = styled.div.withConfig({
  shouldForwardProp: (prop) => !["isOpen"].includes(prop),
})<{ isOpen: boolean }>`
  display: ${({ isOpen }) => (isOpen ? "flex" : "none")};
  font-weight: ${({ theme }) => theme.fontWeights.light};
  margin-bottom: ${({ theme }) => theme.spacing(1)};
  font-size: ${({ theme }) => theme.fontSizes.xs};
  color: ${({ theme }) => theme.colors.white};
  width: 100%;
`;

/**
 * Container de ações do DatePicker (Mobile)
 * Exibe os botões de ação no rodapé do componente.
 */
export const DatePickerActionsMobile = styled.div`
  gap: ${({ theme }) => theme.spacing(1)};
  justify-content: space-between;
  align-items: center;
  display: flex;
  width: 100%;
`;

/**
 * Container de datas selecionadas (Mobile)
 */
export const SelectedDatesContainerMobile = styled.div`
  padding: ${({ theme }) => theme.spacing(1)}
    ${({ theme }) => theme.spacing(1.5)};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  background-color: ${({ theme }) => theme.colors.white};
  min-width: calc(100% - ${({ theme }) => theme.spacing(12)});
  height: ${({ theme }) => theme.spacing(6)};
  gap: ${({ theme }) => theme.spacing(1)};
  align-items: center;
  flex-wrap: wrap;
  display: flex;
`;

/**
 * Container do campo de entrada (Mobile)
 * Define estilos flexíveis e ajustáveis com base no estado.
 */
export const InputContainerMobile = styled.div.withConfig({
  shouldForwardProp: (prop) => !["isOpen"].includes(prop),
})<{ isOpen: boolean }>`
  padding: ${({ theme, isOpen }) =>
    isOpen
      ? `${theme.spacing(0.5)} ${theme.spacing(2.5)} ${theme.spacing(1)}`
      : 0};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  font-size: ${({ theme }) => theme.fontSizes.md};
  align-items: center;
  position: relative;
  height: inherit;
  flex-wrap: wrap;
  cursor: pointer;
  display: flex;
  flex-direction: row;

  &::placeholder {
    font-weight: ${({ theme }) => theme.fontWeights.medium};
    color: ${({ theme }) => theme.colors.darkGray};
  }
`;

/**
 * Wrapper do conteúdo do campo de entrada (Mobile)
 * Organiza o ícone e o texto do placeholder.
 */
export const WrapperInputMobile = styled.div.withConfig({
  shouldForwardProp: (prop) =>
    !["isVisible"].includes(prop) && !["selectedDates"].includes(prop),
})<{ isVisible: boolean; selectedDates: boolean }>`
  gap: ${({ theme }) => theme.spacing(2)};
  align-items: center;
  height: inherit;
  width: 100%;
  display: flex;

  & > svg {
    color: ${({ theme, selectedDates }) =>
      !selectedDates ? theme.colors.yellow : theme.colors.white};
    padding-bottom: ${({ theme }) => theme.spacing(0.75)};
    width: ${({ theme }) => theme.spacing(4)};
    height: ${({ theme }) => theme.spacing(6)};

    & path {
      fill: ${({ theme, isVisible }) => (isVisible ? theme.colors.white : "")};
    }
  }
`;

/**
 * Placeholder no campo de entrada (Mobile)
 * Configura o texto exibido quando nenhuma data está selecionada.
 */
export const PlaceholderMobile = styled.span.withConfig({
  shouldForwardProp: (prop) => !["isOpen"].includes(prop),
})<{ isOpen: boolean }>`
  font-size: ${({ theme, isOpen }) =>
    isOpen ? theme.fontSizes.lg : theme.fontSizes.sm};
  color: ${({ theme, isOpen }) =>
    isOpen ? theme.colors.white : theme.colors.darkGray};
  font-weight: ${({ theme, isOpen }) =>
    isOpen ? theme.fontWeights.medium : theme.fontWeights.medium};
  display: ${({ isOpen }) => (isOpen ? "flex" : "flex")};
  padding-top: ${({ theme }) => theme.spacing(0.5)};
`;

/**
 * Ícone de hover (Mobile)
 * Exibido ao passar o mouse sobre uma data selecionada.
 */
export const HoverIconMobile = styled.div`
  width: ${({ theme }) => theme.spacing(3)};
  height: ${({ theme }) => theme.spacing(3)};
  transform: translate(-50%, -50%);
  transition: all 0.3s ease-in-out;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: 50%;
  left: 50%;
  display: none;
  opacity: 0.9;
`;

/**
 * Badge de data selecionada (Mobile)
 * Estiliza as datas selecionadas como badges interativos.
 */
export const SelectedDateMobile = styled.div.withConfig({
  shouldForwardProp: (prop) => !["type"].includes(prop),
})<{ type: string }>`
  background-color: ${({ type, theme }) =>
    type === "destination" ? theme.colors.mediumBlue : theme.colors.yellow};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  padding: ${({ theme }) => theme.spacing(1)}
    ${({ theme }) => theme.spacing(1.2)};
  font-size: ${({ theme }) => theme.spacing(1.5)};
  color: ${({ theme }) => theme.colors.black};
  transition: all 0.1s ease-in-out;
  text-transform: uppercase;
  align-items: center;
  position: relative;
  line-height: 1.2;
  cursor: pointer;
  display: flex;

  &:hover {
    background-color: ${({ theme }) => theme.colors.lightRed};
    color: ${({ theme }) => theme.colors.lightRed};

    ${HoverIconMobile} {
      display: flex;
    }

    & > svg {
      width: 0;
      height: 0;
    }
  }

  & > svg {
    top: ${({ type }) => (type === "destination" ? "" : "0")};
    right: ${({ type }) => (type === "destination" ? "" : "0")};
    bottom: ${({ type }) => (type === "destination" ? "0" : "")};
    left: ${({ type }) => (type === "destination" ? "0" : "")};
    color: ${({ theme }) => theme.colors.black};
    width: ${({ theme }) => theme.spacing(1)};
    height: ${({ theme }) => theme.spacing(1)};
    transition: all 0.3s ease-in-out;
    position: absolute;
    stroke-width: 1.5;
  }
`;

/**
 * Wrapper de datas selecionadas (Mobile)
 */
export const WrapperSelectedDatesMobile = styled.div`
  gap: ${({ theme }) => theme.spacing(1)};
  align-items: center;
  flex-wrap: wrap;
  display: flex;
`;

/**
 * Ícone de hover
 * Ícone exibido quando o usuário passa o mouse sobre uma data selecionada.
 */
export const HoverIcon = styled.div`
  width: ${({ theme }) => theme.spacing(3)};
  height: ${({ theme }) => theme.spacing(3)};
  transform: translate(-50%, -50%);
  transition: all 0.3s ease-in-out;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: 50%;
  left: 50%;
  display: none;
  opacity: 0.9;
`;

/**
 * Ícone de edição
 * Ícone exibido para editar as datas selecionadas.
 */
export const EditIcon = styled.div.withConfig({
  shouldForwardProp: (prop) => !["selectedDates"].includes(prop),
})<{ selectedDates: boolean }>`
  color: ${({ theme, selectedDates }) =>
    selectedDates ? theme.colors.yellow : theme.colors.white};
  width: ${({ theme }) => theme.spacing(4.5)};
  justify-content: center;
  position: absolute;
  display: flex;
  right: 0;

  & > svg {
    color: ${({ theme, selectedDates }) =>
      !selectedDates ? theme.colors.yellow : theme.colors.white};
  }
`;

/**
 * Badge de data selecionada (Desktop)
 * Estiliza as datas selecionadas como badges interativos.
 */
export const SelectedDate = styled.div.withConfig({
  shouldForwardProp: (prop) => !["type"].includes(prop),
})<{ type: string }>`
  background-color: ${({ type, theme }) =>
    type === "destination" ? theme.colors.mediumBlue : theme.colors.yellow};
  border-radius: ${({ type, theme }) =>
    type === "destination"
      ? `${theme.spacing(0.5)} ${theme.spacing(0.5)} ${theme.spacing(0.5)} ${theme.spacing(0.1)}`
      : `${theme.spacing(0.5)} ${theme.spacing(0.1)} ${theme.spacing(0.5)} ${theme.spacing(0.5)}`};
  color: ${({ theme }) => theme.colors.black};
  padding: ${({ theme }) => theme.spacing(0.75)}
    ${({ theme }) => theme.spacing(1.25)};
  font-size: ${({ theme }) => theme.spacing(1.75)};
  transition: all 0.1s ease-in-out;
  text-transform: uppercase;
  flex-direction: column;
  align-items: center;
  position: relative;
  line-height: 1.1;
  cursor: pointer;
  display: flex;
  z-index: 1000;

  & > svg {
    color: black !important;
    top: ${({ type }) => (type === "destination" ? "" : "0")};
    right: ${({ type }) => (type === "destination" ? "" : "0")};
    bottom: ${({ type }) => (type === "destination" ? "0" : "")};
    left: ${({ type }) => (type === "destination" ? "0" : "")};
    width: ${({ theme }) => theme.spacing(1)};
    height: ${({ theme }) => theme.spacing(1)};
    transition: all 0.3s ease-in-out;
    position: absolute;
    stroke-width: 1.5;
  }
`;
